import { ShieldCheck, FileText, Building2, MapPin, Navigation, ArrowUpRight, Info } from "lucide-react";
import { usePublicConfig } from "@/hooks/usePublicConfig";
import { buildDirectionsUrl, trackDirectionsClick } from "@/lib/location-links";

/** Public-facing compliance / legal block — driven entirely from the admin Compliance editor. */
export function ComplianceBlock() {
  const { compliance } = usePublicConfig();

  return (
    <section className="py-12 md:py-16 bg-secondary/40 border-t border-border">
      <div className="mx-auto max-w-6xl px-4 md:px-6">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-[11px] font-bold tracking-[0.25em] uppercase text-primary">Compliance & coverage</p>
          <h2 className="mt-2 text-2xl md:text-3xl font-extrabold tracking-tight text-foreground">
            Verified business details
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            All structured data, listings and quotes use the same verified information shown below.
          </p>
        </div>

        <div className="mt-8 grid md:grid-cols-3 gap-4">
          <div className="rounded-xl border border-border bg-card p-5">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Building2 className="h-4 w-4" />
            </div>
            <h3 className="mt-3 text-sm font-bold text-foreground">Company</h3>
            <dl className="mt-2 space-y-1 text-xs text-muted-foreground">
              <div><dt className="inline font-semibold text-foreground">Legal name: </dt><dd className="inline">{compliance.legalName}</dd></div>
              {compliance.companyNumber && (
                <div><dt className="inline font-semibold text-foreground">Company #: </dt><dd className="inline font-mono">{compliance.companyNumber}</dd></div>
              )}
              {compliance.vatNumber && (
                <div><dt className="inline font-semibold text-foreground">VAT: </dt><dd className="inline font-mono">{compliance.vatNumber}</dd></div>
              )}
              <div><dt className="inline font-semibold text-foreground">DVSA VTS: </dt><dd className="inline font-mono">{compliance.vtsNumber}</dd></div>
              <div><dt className="inline font-semibold text-foreground">MOT classes: </dt><dd className="inline">{compliance.motClasses}</dd></div>
            </dl>
          </div>

          <div className="rounded-xl border border-border bg-card p-5">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <MapPin className="h-4 w-4" />
            </div>
            <h3 className="mt-3 text-sm font-bold text-foreground">Registered & entrance</h3>
            <dl className="mt-2 space-y-1.5 text-xs">
              <div>
                <dt className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Registered</dt>
                <dd className="text-foreground font-medium">{compliance.streetAddress}, {compliance.addressLocality} {compliance.postalCode}</dd>
                <a
                  href={buildDirectionsUrl("registered")}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackDirectionsClick({ source: "compliance_block", kind: "registered" })}
                  className="mt-1 inline-flex items-center gap-1 text-[11px] font-bold text-muted-foreground hover:text-primary transition-colors"
                >
                  Directions to registered <ArrowUpRight className="h-3 w-3" />
                </a>
              </div>
              <div className="rounded-lg border border-primary/20 bg-primary/5 p-2.5 mt-2">
                <dt className="text-[10px] font-bold uppercase tracking-wider text-primary">Yard entrance (use this for sat-nav)</dt>
                <dd className="text-foreground font-semibold mt-0.5">14 Copenhagen Place, London E14 7DF</dd>
                <dd className="text-[11px] text-muted-foreground mt-1">51.514512, -0.029928</dd>
                <a
                  href={buildDirectionsUrl("entrance")}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackDirectionsClick({ source: "compliance_block", kind: "entrance" })}
                  className="mt-2 inline-flex items-center gap-1 text-[11px] font-bold text-primary hover:text-primary/80 transition-colors"
                >
                  <Navigation className="h-3 w-3" /> Directions to yard entrance <ArrowUpRight className="h-3 w-3" />
                </a>
              </div>
            </dl>
            <p
              className="mt-3 inline-flex items-start gap-1.5 text-[11px] text-muted-foreground leading-relaxed cursor-help"
              title="Pixley Street is the DVSA / Companies House registered address. Copenhagen Place is the gated yard you actually drive into. Always sat-nav to Copenhagen Place."
            >
              <Info className="h-3 w-3 shrink-0 mt-0.5 text-primary/70" />
              <span>Why two addresses? Pixley St is our legal/registered address. Copenhagen Pl is the workshop entrance — use that one for sat-nav.</span>
            </p>
            {compliance.entranceInstructions && (
              <p className="mt-2 text-xs text-muted-foreground leading-relaxed">{compliance.entranceInstructions}</p>
            )}
          </div>

          <div className="rounded-xl border border-border bg-card p-5">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <ShieldCheck className="h-4 w-4" />
            </div>
            <h3 className="mt-3 text-sm font-bold text-foreground">Service coverage</h3>
            <ul className="mt-2 flex flex-wrap gap-1">
              {compliance.serviceCoverage.slice(0, 12).map((area) => (
                <li key={area} className="text-[11px] px-2 py-0.5 rounded bg-secondary text-foreground">{area}</li>
              ))}
            </ul>
          </div>
        </div>

        {compliance.disclaimers.length > 0 && (
          <div className="mt-6 rounded-xl border border-border bg-card p-5">
            <div className="flex items-center gap-2">
              <FileText className="h-3.5 w-3.5 text-primary" />
              <h3 className="text-xs font-bold uppercase tracking-wider text-foreground">Disclaimers</h3>
            </div>
            <ul className="mt-3 space-y-2">
              {compliance.disclaimers.map((d, i) => (
                <li key={i} className="text-xs text-muted-foreground leading-relaxed">{d}</li>
              ))}
            </ul>
            <p className="mt-3 text-[11px] text-muted-foreground">Last reviewed: {compliance.lastReviewed}</p>
          </div>
        )}
      </div>
    </section>
  );
}
